// @flow

import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { getRouteParam } from "@selectors";
import * as sharedSelectors from "@modules/shared/selectors";
import type { SpecialContactType } from "@modules/shared/reducer/special-contact-types/types";
import actionCreator from "@modules/service/actionCreator";
import { SectionComponent, GSGrid, Button, View } from "@components";
import {
  AlertCriteriaView,
  AlertContentForm,
  AlertEmailAttachmentsForm,
  AlertAbsenceReasonsView,
  AlertRecipientsView,
  AlertBespokeRecipientsView,
  AlertServiceLengthView,
  AlertSuppression,
  AlertsEditModal,
} from "@modules/service/components";
import type { State } from "@store/reducers";
import type { Alert } from "@modules/service/reducer/alerts/types";

type OwnProps = $Exact<{
  clientId: number,
  data: Alert,
  schema: Object,
}>;

type PropsFromState = $Exact<{
  specialContactTypes: Array<SpecialContactType>,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
  getCurrentAlert: typeof actionCreator.GetAlert,
  getSchema: typeof actionCreator.GetAlertSchema,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export function AlertDetailsView(props: Props) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(0);

  const { clientId } = props;
  useEffect(() => {
    props.getSchema({ clientId });
  }, []);

  const toggleEditModal = () => {
    setIsEditModalOpen(prev => !prev);
  };

  const handleEditStep = (step: number) => {
    return () => {
      props.setDraft(props.data);
      setModalStep(step);
      toggleEditModal();
    };
  };

  if (!props.data) {
    return null;
  }

  return (
    <View gap="6">
      {/* Criteria */}
      <View className="gs-container" flex={1}>
        <SectionComponent
          title={<h6>{props.t("ALERT_DETAILS_CRITERIA")}</h6>}
          rightLink={
            <Button materialIcon="edit" onClick={handleEditStep(1)}>
              {props.t("EDIT")}
            </Button>
          }
        >
          <AlertCriteriaView data={props.data} clientId={props.clientId} />
        </SectionComponent>
      </View>
      {/* Content */}
      <View className="gs-container" flex={1}>
        <SectionComponent
          title={<h6>{props.t("CONTENT")}</h6>}
          rightLink={
            <Button materialIcon="edit" onClick={handleEditStep(2)}>
              {props.t("EDIT")}
            </Button>
          }
        >
          <View gap={4}>
            <AlertContentForm
              data={props.data.content.emailContent}
              schema={props.schema?.properties?.emailContent || {}}
            />
            <AlertEmailAttachmentsForm
              data={props.data.content.attachments}
              schema={props.schema}
              clientId={props.clientId}
              alertId={props.data.id}
            />
          </View>
        </SectionComponent>
      </View>
      {/* Absence Reason */}
      <View className="gs-container" flex={1}>
        <SectionComponent
          title={<h6>{props.t("ABSENCE_REASONS")}</h6>}
          rightLink={
            <Button materialIcon="edit" onClick={handleEditStep(3)}>
              {props.t("EDIT")}
            </Button>
          }
        >
          <AlertAbsenceReasonsView data={props.data.content} />
        </SectionComponent>
      </View>
      {/* Alert suppression */}
      <AlertSuppression clientId={props.clientId} alertId={props.data.id} />
      {/* Recepients */}
      <View className="gs-container" flex={1}>
        <SectionComponent
          title={<h6>{props.t("RECIPIENTS")}</h6>}
          rightLink={
            <Button materialIcon="edit" onClick={handleEditStep(4)}>
              {props.t("EDIT")}
            </Button>
          }
        >
          <View gap={4}>
            <AlertRecipientsView
              data={props.data.content}
              specialContactTypes={props.specialContactTypes}
            />
            <AlertBespokeRecipientsView data={props.data.content} />
          </View>
        </SectionComponent>
      </View>
      {/* Staff groups */}
      <View className="gs-container" flex={1}>
        <SectionComponent
          title={<h6>{props.t("ALERT_DETAILS_STAFF_GROUPS")}</h6>}
          rightLink={
            <Button materialIcon="edit" onClick={handleEditStep(5)}>
              {props.t("EDIT")}
            </Button>
          }
        >
          <GSGrid
            columns={[
              {
                name: props.t("ALERT_DETAILS_STAFF_GROUPS"),
                key: "name",
                align: "left",
              },
            ]}
            rows={props?.data?.content?.staffGroups}
            noResultsText={props.t("NO_STAFF_GROUP_RESTIRCTIONS_MESSAGE")}
          />
        </SectionComponent>
      </View>
      {/* Service length */}
      <SectionComponent
        title={<h6>{props.t("ALERT_DETAILS_SERVICE_LENGTH")}</h6>}
        rightLink={
          <Button materialIcon="edit" onClick={handleEditStep(6)}>
            {props.t("EDIT")}
          </Button>
        }
        className="gs-container"
      >
        <AlertServiceLengthView data={props.data.content} />
      </SectionComponent>
      {isEditModalOpen && (
        <AlertsEditModal
          clientId={props.clientId}
          defaultStep={modalStep}
          editing
          container={props.data}
          onCancel={toggleEditModal}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  specialContactTypes: sharedSelectors.getSpecialContactTypes(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAlertDraft,
  getCurrentAlert: actionCreator.GetAlert,
  getSchema: actionCreator.GetAlertSchema,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AlertDetailsView);
